import 'element.quantity-selector'

class HeaderCartDrawer extends HTMLElement {
  constructor() {
    super()
    this.locales = {}
    this._observer = null
    this._onSubmit = this._onSubmit.bind(this)
    this._onClick = this._onClick.bind(this)
    this._onChange = this._onChange.bind(this)
    this._onCartRefresh = this._onCartRefresh.bind(this)
    this._refreshing = false
    this._changing = new Map()
  }

  connectedCallback() {
    const form = this.querySelector('form')
    if (form) form.addEventListener('submit', this._onSubmit)

    const localesScript = this.querySelector('[data-locales]')
    if (localesScript) {
      try {
        this.locales = JSON.parse(localesScript.textContent || '{}') || {}
      } catch (e) {
        this.locales = {}
      }
    }

    this._setupObserver()

    this.addEventListener('click', this._onClick, true)
    this.addEventListener('change', this._onChange)

    document.addEventListener('cart:refresh', this._onCartRefresh)

    this._refreshCart()
  }

  disconnectedCallback() {
    const form = this.querySelector('form')
    if (form) form.removeEventListener('submit', this._onSubmit)

    this.removeEventListener('click', this._onClick, true)
    this.removeEventListener('change', this._onChange)

    document.removeEventListener('cart:refresh', this._onCartRefresh)

    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }

    this._changing.clear()
  }

  _onCartRefresh() {
    this._refreshCart()
  }

  _setupObserver() {
    const target = this.querySelector('[data-products]')
    if (!target) return

    if (this._observer) this._observer.disconnect()

    this._observer = new MutationObserver(() => {
      this._syncEmptyState()
    })

    this._observer.observe(target, { childList: true, subtree: true })
    this._syncEmptyState()
  }

  _syncEmptyState() {
    const items = this.querySelector('[data-products] .cart__items')
    const countRaw = items ? items.getAttribute('data-count') : null
    const count = countRaw ? parseInt(countRaw, 10) : 0
    const isEmpty = !count || count <= 0

    if (isEmpty) this.classList.add('is-empty')
    else this.classList.remove('is-empty')
  }

  async _refreshCart() {
    if (this._refreshing) return
    this._refreshing = true

    try {
      const root = window.Shopify?.routes?.root || '/'
      const res = await fetch(`${root}?sections=cart-ajax`, { credentials: 'same-origin' })
      if (!res.ok) return
      const json = await res.json()
      const html = json?.['cart-ajax']
      if (!html) return
      this._applyHtml(html)
    } finally {
      this._refreshing = false
    }
  }

  _applyHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    const items = doc.querySelector('.cart__items')
    const discounts = doc.querySelector('.cart__discounts')
    const subtotal = doc.querySelector('.cart__subtotal')
    /* SE - AJAX loyalty - 18 Mar '26' */
    const loyaltyTotal = doc.querySelector('.cart__loyalty-total')
    const bubble = doc.querySelector('.cart-link__bubble')
    const header = doc.querySelector('.cart__header')

    const productsTarget = this.querySelector('[data-products]')
    if (productsTarget && items) productsTarget.innerHTML = items.outerHTML

    const discountsTarget = this.querySelector('[data-discounts]')
    if (discountsTarget) discountsTarget.innerHTML = discounts ? discounts.outerHTML : ''

    const subtotalTarget = this.querySelector('[data-subtotal]')
    if (subtotalTarget && subtotal) subtotalTarget.innerHTML = subtotal.innerHTML

    /* SE - Drawer sync - 18 Mar '26' */
    const loyaltyTarget = this.querySelector('.cart__loyalty-total')
    if (loyaltyTarget && loyaltyTotal) {
      loyaltyTarget.innerHTML = loyaltyTotal.innerHTML
      if (loyaltyTotal.hasAttribute('data-loyalty-balance')) {
        loyaltyTarget.setAttribute('data-loyalty-balance', '')
      } else {
        loyaltyTarget.removeAttribute('data-loyalty-balance')
      }
      const isLoggedIn = loyaltyTotal.getAttribute('data-customer-logged-in')
      if (isLoggedIn != null) loyaltyTarget.setAttribute('data-customer-logged-in', isLoggedIn)
      else loyaltyTarget.removeAttribute('data-customer-logged-in')
      const loyaltyTemplate = loyaltyTotal.getAttribute('data-loyalty-template')
      if (loyaltyTemplate != null) loyaltyTarget.setAttribute('data-loyalty-template', loyaltyTemplate)
      else loyaltyTarget.removeAttribute('data-loyalty-template')
      const loyaltyTemplateWithAmount = loyaltyTotal.getAttribute('data-loyalty-template-with-amount')
      if (loyaltyTemplateWithAmount != null) loyaltyTarget.setAttribute('data-loyalty-template-with-amount', loyaltyTemplateWithAmount)
      else loyaltyTarget.removeAttribute('data-loyalty-template-with-amount')
    }

    const headerTarget = this.querySelector('[data-header]')
    if (headerTarget && header) headerTarget.innerHTML = header.outerHTML

    if (bubble) {
      document.querySelectorAll('.cart-link__bubble').forEach((el) => {
        el.outerHTML = bubble.outerHTML
      })
    }

    this._ensureQtyButtonFallbacks()
    this._syncEmptyState()
    /* SE - Loyalty refresh - 18 Mar '26' */
    if (window && typeof window.hcQueueGridLoyaltyPointsInit === 'function') {
      window.hcQueueGridLoyaltyPointsInit(this)
    }
  }

  _ensureQtyButtonFallbacks() {
    this.querySelectorAll('quantity-selector').forEach((qs) => {
      const minus = qs.querySelector('button.element-quantity-selector__button--minus')
      const plus = qs.querySelector('button.element-quantity-selector__button--plus')

      if (minus && !minus.querySelector('svg') && (minus.textContent || '').trim() === '') {
        minus.textContent = '−'
      }
      if (plus && !plus.querySelector('svg') && (plus.textContent || '').trim() === '') {
        plus.textContent = '+'
      }
    })
  }

  _getKeyFromTarget(target) {
    const qs = target.closest('quantity-selector[key]')
    if (qs) {
      const k = qs.getAttribute('key')
      if (k) return k
    }

    const item = target.closest('.cart__item[data-key]')
    if (item) {
      const k = item.getAttribute('data-key')
      if (k) return k
    }

    return null
  }

  _getQtyInputFromTarget(target) {
    const qs = target.closest('quantity-selector')
    if (qs) {
      const input = qs.querySelector('.element-quantity-selector__input')
      if (input) return input
    }

    const item = target.closest('.cart__item')
    if (!item) return null
    return item.querySelector('.element-quantity-selector__input, input[name="updates[]"]')
  }

  async _change(key, quantity, loadingEl) {
    if (!key) return
    const prev = this._changing.get(key)
    if (prev) prev.abort()

    const controller = new AbortController()
    this._changing.set(key, controller)

    if (loadingEl) loadingEl.classList.add('is-loading')

    try {
      const root = window.Shopify?.routes?.root || '/'
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: ['cart-ajax'],
          sections_url: window.location.pathname
        }),
        signal: controller.signal,
        credentials: 'same-origin'
      })

      if (!res.ok) return

      const state = await res.json()
      const html = state?.sections?.['cart-ajax']
      if (!html) return

      this._applyHtml(html)
    } catch (e) {
      if (e && e.name === 'AbortError') return
    } finally {
      if (loadingEl) loadingEl.classList.remove('is-loading')
      const current = this._changing.get(key)
      if (current === controller) this._changing.delete(key)
    }
  }

  _onClick(e) {
    const btn = e.target.closest('button.element-quantity-selector__button')
    if (!btn) return

    const qs = btn.closest('quantity-selector')
    if (!qs) return

    const isPlus = btn.classList.contains('element-quantity-selector__button--plus')
    const isMinus = btn.classList.contains('element-quantity-selector__button--minus')
    if (!isPlus && !isMinus) return

    const key = this._getKeyFromTarget(btn)
    const input = this._getQtyInputFromTarget(btn)
    if (!key || !input) return

    e.preventDefault()
    e.stopPropagation()

    const raw = parseInt(input.value, 10)
    const current = Number.isFinite(raw) ? raw : 0
    const next = Math.max(0, current + (isPlus ? 1 : -1))

    input.value = String(next)
    this._change(key, next, qs)
  }

  _onChange(e) {
    const input = e.target.closest('.element-quantity-selector__input, input[name="updates[]"]')
    if (!input) return

    const key = this._getKeyFromTarget(input)
    if (!key) return

    const qs = input.closest('quantity-selector')

    const raw = parseInt(input.value, 10)
    const qty = Math.max(0, Number.isFinite(raw) ? raw : 0)

    input.value = String(qty)
    this._change(key, qty, qs)
  }

  _onSubmit(e) {
    const form = e.currentTarget
    if (!form) return

    const termsRequired = form.getAttribute('data-terms-required')
    if (termsRequired !== 'true') return

    const checkbox = form.querySelector('input[name="CartTerms"]')
    if (checkbox && checkbox.checked) return

    e.preventDefault()

    const msg =
      (this.locales && this.locales.cartTermsConfirmation) ||
      'You must agree with the terms and conditions of sales to check out'

    alert(msg)
  }
}

if (!customElements.get('header-cart-drawer')) {
  customElements.define('header-cart-drawer', HeaderCartDrawer)
}
